import axios from 'axios';
import { getApiUrl } from '../config';

/**
 * KYC Status values from the user-service
 */
export type KYCStatusValue =
    | 'PENDING'          // User has not started KYC
    | 'IN_REVIEW'        // Documents submitted, awaiting review
    | 'ACTION_REQUIRED'  // User must provide more info/docs
    | 'ON_HOLD'          // Manual review or compliance hold
    | 'APPROVED'         // KYC passed
    | 'REJECTED'         // KYC failed, retry allowed
    | 'REJECTED_FINAL'   // KYC failed, no retry allowed
    | 'SUSPENDED'        // Manually suspended by admin
    | 'EXPIRED';         // KYC expired (if expiry enabled)

/**
 * Sanctions screening status
 */
export type SanctionsStatus = 'CLEAR' | 'HIT' | 'REVIEW_REQUIRED';

/**
 * KYC Status response from the API
 */
export interface KYCStatus {
    status: KYCStatusValue;
    level: string;
    canTrade: boolean;
    rejectionReason: string | null;
    verifiedAt: string | null;
    expiresAt: string | null;
    sanctionsStatus: SanctionsStatus;
}

/**
 * KYC Session response from the API
 */
export interface KYCSession {
    accessToken: string;
    expiresAt: string;
}

/**
 * Helper to check if KYC status allows retry
 */
export function canRetryKYC(status: KYCStatusValue): boolean {
    return ['PENDING', 'ACTION_REQUIRED', 'REJECTED', 'EXPIRED'].includes(status);
}

/**
 * Helper to check if KYC is in a pending/review state
 */
export function isKYCPending(status: KYCStatusValue): boolean {
    return ['PENDING', 'IN_REVIEW', 'ACTION_REQUIRED', 'ON_HOLD'].includes(status);
}

/**
 * Helper to get user-friendly status message
 */
export function getKYCStatusMessage(status: KYCStatusValue): string {
    const messages: Record<KYCStatusValue, string> = {
        PENDING: 'Please verify your identity to start trading',
        IN_REVIEW: 'Your verification is being reviewed',
        ACTION_REQUIRED: 'Additional information required',
        ON_HOLD: 'Your verification is under manual review',
        APPROVED: 'Identity verified',
        REJECTED: 'Verification failed. Please try again.',
        REJECTED_FINAL: 'Verification failed. Please contact support.',
        SUSPENDED: 'Account suspended. Please contact support.',
        EXPIRED: 'Verification expired. Please verify again.',
    };
    return messages[status];
}

/**
 * Fetch the current KYC status for the authenticated user.
 * Routes through matching-engine-ui-backend which proxies to user-service.
 */
export async function getKYCStatus(token: string): Promise<KYCStatus> {
    const response = await axios.get<KYCStatus>(
        `${getApiUrl('javaBackend')}/api/v1/kyc/status`,
        {
            headers: {
                Authorization: `Bearer ${token}`
            }
        }
    );
    return response.data;
}

/**
 * Create a new KYC session for Sumsub WebSDK.
 * Routes through matching-engine-ui-backend which proxies to user-service.
 */
export async function createKYCSession(token: string, email: string): Promise<KYCSession> {
    const response = await axios.post<KYCSession>(
        `${getApiUrl('javaBackend')}/api/v1/kyc/session`,
        { email },
        {
            headers: {
                Authorization: `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        }
    );
    return response.data;
}

/**
 * Refresh the KYC session token.
 * Routes through matching-engine-ui-backend which proxies to user-service.
 */
export async function refreshKYCSession(token: string): Promise<KYCSession> {
    const response = await axios.post<KYCSession>(
        `${getApiUrl('javaBackend')}/api/v1/kyc/session/refresh`,
        {},
        {
            headers: {
                Authorization: `Bearer ${token}`
            }
        }
    );
    return response.data;
}
